import { useSyncExternalStore } from 'react';

import useUser from '../../hooks/useUser';

// Who can use Gargi: the Gargi page, Ask Gargi and Gargi on other screens. One rule, the same as api-server's
// requireGargiAccess, so access widens in one place: today CASA staff; next, super admins of businesses subscribed
// to Gargi.
export const canUseGargi = (user?: { _isInternalUser?: boolean } | null) => !!user?._isInternalUser;

export const useGargiAccess = () => canUseGargi(useUser().currentUser);

// Gargi's AI (Claude: Ask Gargi, summaries worded by Gargi, reading files) is an add-on. Everything else on Growth
// Desk and the Gargi page — CASA's rules, the models, targets, what worked — works without it. Staff can switch the
// AI off in their own browser to see the pages as a business without the add-on; the subscription check comes here.
const AI_OFF_KEY = 'casa.gargi-ai-off';
const listeners = new Set<() => void>();
let memo: boolean | null = null;

const readAiOff = () => {
  try {
    return window.localStorage.getItem(AI_OFF_KEY) === '1';
  } catch {
    return false;
  }
};

const aiOff = () => (memo ??= readAiOff());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const setGargiAiOff = (off: boolean) => {
  try {
    if (off) window.localStorage.setItem(AI_OFF_KEY, '1');
    else window.localStorage.removeItem(AI_OFF_KEY);
  } catch {
    // Storage blocked: the switch lasts until the page reloads.
  }
  memo = off;
  listeners.forEach((l) => l());
};

export const useGargiAiOff = () => useSyncExternalStore(subscribe, aiOff, () => false);

// True when Gargi's AI can be offered: the person has Gargi and has not switched its AI off.
export const useGargiAi = () => {
  const allowed = useGargiAccess();
  const off = useGargiAiOff();
  return allowed && !off;
};
